import { useCallback, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import IconButton from '@mui/material/IconButton';
import { alpha, styled } from '@mui/material/styles';
import { AlertSeverity } from '@utils/enums';
import { INotification } from './types';

type Severity = `${AlertSeverity}`;

interface IStyledAlertProps {
  severity: Severity;
}

interface INotificationBannerProps {
  action?: JSX.Element;
  notifications: INotification[];
}

const StyledAlert = styled(Alert)<IStyledAlertProps>(({ severity, theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1),
  whiteSpace: 'pre-line',

  '&.MuiPaper-root': {
    backgroundColor: alpha(
      theme.palette[severity || AlertSeverity.Info].light,
      0.2,
    ),
  },

  '&:first-of-type': {
    marginTop: theme.spacing(-2),
  },

  '& .MuiAlert-action': {
    alignSelf: 'center',
  },
}));

const StyledTitle = styled(AlertTitle)({ fontSize: '1.1rem' });

const StyledCloseButton = styled(IconButton, {
  shouldForwardProp: prop => prop !== 'hasAction',
})<{ hasAction: boolean }>(({ hasAction }) => ({
  ...(hasAction && {
    marginLeft: '10px',
    marginTop: '5px',
  }),
}));

export const NotificationBanner = ({
  action,
  notifications,
}: INotificationBannerProps) => {
  const [dismissedIds, setDismissedIds] = useState<Set<string | number>>(
    new Set(),
  );

  const handleClose = useCallback(
    (id: string | number) => () => {
      setDismissedIds(prev => new Set(prev).add(id));
    },
    [setDismissedIds],
  );

  return (
    <>
      {notifications.map((notification, index) => {
        const {
          id: notificationId,
          isClosable = false,
          message,
          severity,
          title,
        } = notification;
        const id = notificationId ?? index;

        if (isClosable && dismissedIds.has(id)) {
          return null;
        }

        return (
          <StyledAlert
            action={
              isClosable ? (
                <>
                  {action}
                  <StyledCloseButton
                    aria-label="close"
                    hasAction={!!action}
                    onClick={handleClose(id)}
                    size="small"
                  >
                    <CloseIcon fontSize="inherit" />
                  </StyledCloseButton>
                </>
              ) : (
                action
              )
            }
            key={id}
            severity={severity}
          >
            {title && <StyledTitle>{title}</StyledTitle>}
            {message}
          </StyledAlert>
        );
      })}
    </>
  );
};
